import {
  createPurchasingUnit,
  getPurchasingUnits,
  getUserPurchasingUnits,
  syncPurchasingUnits,
  updatePurchasingUnit,
  updateUserPurchasingUnits,
  getEligiblePurchasingUnits,
  getPurchasingUnitFilterOptions,
} from '@/resources/purchasingUnits';

const types = {
  UNITS: 'PURCHASING_UNITS',
  PAGINATION: 'PURCHASING_UNITS_PAGINATION',
  LOADING: 'PURCHASING_UNITS_LOADING',
  ERROR: 'PURCHASING_UNITS_ERROR',
  USER_UNITS: 'USER_PURCHASING_UNITS',
  ELIGIBLE: 'PURCHASING_UNITS_ELIGIBLE',
  ELIGIBLE_LOADING: 'PURCHASING_UNITS_ELIGIBLE_LOADING',
  ELIGIBLE_ERROR: 'PURCHASING_UNITS_ELIGIBLE_ERROR',
  FILTER_OPTIONS: 'PURCHASING_UNITS_FILTER_OPTIONS',
  FILTER_OPTIONS_COMPANY: 'PURCHASING_UNITS_FILTER_OPTIONS_COMPANY',
  FILTER_OPTIONS_ERROR: 'PURCHASING_UNITS_FILTER_OPTIONS_ERROR',
  FILTER_OPTIONS_LOADING: 'PURCHASING_UNITS_FILTER_OPTIONS_LOADING',
};

const idsFrom = values =>
  (Array.isArray(values) ? values : [])
    .map(value => (value && typeof value === 'object' ? value.id : value))
    .filter(id => id !== undefined && id !== null);

const normalizeUserUnits = data => {
  if (Array.isArray(data)) return { purchasingUnitIds: idsFrom(data) };

  const userUnits = data || {};
  const ids = userUnits.purchasingUnitIds || userUnits.ids || userUnits.purchasingUnits;

  return {
    ...userUnits,
    purchasingUnitIds: idsFrom(ids),
  };
};

const ALL_PURCHASING_UNITS_PAGE_SIZE = 100;
const ALL_PURCHASING_UNITS_BATCH_SIZE = 5;

let purchasingUnitsRequestToken = 0;

const nextPurchasingUnitsRequestToken = () => {
  purchasingUnitsRequestToken += 1;
  return purchasingUnitsRequestToken;
};

const isCurrentPurchasingUnitsRequest = requestToken =>
  requestToken === purchasingUnitsRequestToken;

const fetchPurchasingUnitsPage = async (buyerId, pageNumber, pageSize) => {
  const response = await getPurchasingUnits(buyerId, { pageNumber, pageSize });
  const payload = response.data || {};

  return {
    units: Array.isArray(payload.data) ? payload.data : [],
    pagination: payload.meta && payload.meta.pagination,
  };
};

const totalPagesFrom = (pagination, pageSize) => {
  const totalPages = Number(pagination?.totalPages);
  if (Number.isFinite(totalPages) && totalPages > 0) return totalPages;

  const totalElements = Number(pagination?.totalElements);
  if (Number.isFinite(totalElements) && totalElements >= 0) {
    return Math.max(1, Math.ceil(totalElements / pageSize));
  }

  return null;
};

const appendPages = (units, pages) =>
  pages.reduce((allUnits, page) => [...allUnits, ...page.units], units);

const fetchKnownPagesInBatches = async (buyerId, pageNumbers, pageSize, requestToken, units) => {
  if (!pageNumbers.length || !isCurrentPurchasingUnitsRequest(requestToken)) return units;

  const batch = pageNumbers.slice(0, ALL_PURCHASING_UNITS_BATCH_SIZE);
  const pages = await Promise.all(
    batch.map(pageNumber => fetchPurchasingUnitsPage(buyerId, pageNumber, pageSize)),
  );
  const nextUnits = appendPages(units, pages);

  return fetchKnownPagesInBatches(
    buyerId,
    pageNumbers.slice(ALL_PURCHASING_UNITS_BATCH_SIZE),
    pageSize,
    requestToken,
    nextUnits,
  );
};

const fetchRemainingPagesInBatches = async (buyerId, pageNumber, pageSize, requestToken, units) => {
  if (!isCurrentPurchasingUnitsRequest(requestToken)) return units;

  const pageNumbers = Array.from(
    { length: ALL_PURCHASING_UNITS_BATCH_SIZE },
    (value, index) => pageNumber + index,
  );
  const pages = await Promise.all(
    pageNumbers.map(nextPageNumber => fetchPurchasingUnitsPage(buyerId, nextPageNumber, pageSize)),
  );
  const nextUnits = appendPages(units, pages);
  const reachedLastPage = pages.some(page => page.units.length < pageSize);

  if (reachedLastPage) return nextUnits;
  return fetchRemainingPagesInBatches(
    buyerId,
    pageNumber + ALL_PURCHASING_UNITS_BATCH_SIZE,
    pageSize,
    requestToken,
    nextUnits,
  );
};

const mutations = {
  [types.UNITS](state, units) {
    state.units = Array.isArray(units) ? units : [];
  },
  [types.PAGINATION](state, pagination) {
    state.pagination = pagination || {};
  },
  [types.LOADING](state, loading) {
    state.loading = loading;
  },
  [types.ERROR](state, error) {
    state.error = error || null;
  },
  [types.USER_UNITS](state, data) {
    state.userUnits = normalizeUserUnits(data);
  },
  [types.ELIGIBLE](state, units) {
    state.eligibleUnits = Array.isArray(units) ? units : [];
  },
  [types.ELIGIBLE_LOADING](state, loading) {
    state.eligibleLoading = loading;
  },
  [types.ELIGIBLE_ERROR](state, error) {
    state.eligibleError = error || null;
  },
  [types.FILTER_OPTIONS](state, units) {
    state.filterOptions = Array.isArray(units) ? units : [];
  },
  [types.FILTER_OPTIONS_COMPANY](state, company) {
    state.filterOptionsCompany = company || null;
  },
  [types.FILTER_OPTIONS_LOADING](state, value) {
    state.filterOptionsLoading = value;
  },
  [types.FILTER_OPTIONS_ERROR](state, error) {
    state.filterOptionsError = error || null;
  },
};

const actions = {
  async fetchPurchasingUnits({ commit }, { buyerId, params }) {
    const requestToken = nextPurchasingUnitsRequestToken();
    commit(types.LOADING, true);
    commit(types.ERROR, null);
    try {
      const response = await getPurchasingUnits(buyerId, params);
      const payload = response.data || {};
      if (isCurrentPurchasingUnitsRequest(requestToken)) {
        commit(types.UNITS, payload.data);
        commit(types.PAGINATION, payload.meta && payload.meta.pagination);
      }
      return payload;
    } catch (error) {
      if (isCurrentPurchasingUnitsRequest(requestToken)) commit(types.ERROR, error);
      throw error;
    } finally {
      if (isCurrentPurchasingUnitsRequest(requestToken)) commit(types.LOADING, false);
    }
  },
  async fetchAllPurchasingUnits(
    { commit },
    { buyerId, pageSize = ALL_PURCHASING_UNITS_PAGE_SIZE },
  ) {
    const requestToken = nextPurchasingUnitsRequestToken();
    commit(types.LOADING, true);
    commit(types.ERROR, null);
    try {
      const normalizedPageSize = Number(pageSize);
      const boundedPageSize =
        Number.isFinite(normalizedPageSize) && normalizedPageSize > 0
          ? Math.min(normalizedPageSize, ALL_PURCHASING_UNITS_PAGE_SIZE)
          : ALL_PURCHASING_UNITS_PAGE_SIZE;
      const firstPage = await fetchPurchasingUnitsPage(buyerId, 1, boundedPageSize);
      const totalPages = totalPagesFrom(firstPage.pagination, boundedPageSize);
      let { units } = firstPage;

      if (!isCurrentPurchasingUnitsRequest(requestToken)) return units;

      if (totalPages && totalPages > 1) {
        const pageNumbers = Array.from({ length: totalPages - 1 }, (value, index) => index + 2);
        units = await fetchKnownPagesInBatches(
          buyerId,
          pageNumbers,
          boundedPageSize,
          requestToken,
          units,
        );
      } else if (!totalPages && firstPage.units.length === boundedPageSize) {
        units = await fetchRemainingPagesInBatches(
          buyerId,
          2,
          boundedPageSize,
          requestToken,
          units,
        );
      }

      if (isCurrentPurchasingUnitsRequest(requestToken)) {
        commit(types.UNITS, units);
        commit(types.PAGINATION, {
          ...(firstPage.pagination || {}),
          pageNumber: 1,
          pageSize: boundedPageSize,
          totalElements: units.length,
        });
      }
      return units;
    } catch (error) {
      if (isCurrentPurchasingUnitsRequest(requestToken)) commit(types.ERROR, error);
      throw error;
    } finally {
      if (isCurrentPurchasingUnitsRequest(requestToken)) commit(types.LOADING, false);
    }
  },
  async createPurchasingUnit({ dispatch }, { buyerId, data, params, refreshAll = false }) {
    const response = await createPurchasingUnit(buyerId, data);
    try {
      if (refreshAll) await dispatch('fetchAllPurchasingUnits', { buyerId });
      else await dispatch('fetchPurchasingUnits', { buyerId, params });
    } catch {
      /* list action records refresh error */
    }
    return response.data;
  },
  async syncPurchasingUnits(_, { buyerId }) {
    const response = await syncPurchasingUnits(buyerId);
    return response.data;
  },
  async updatePurchasingUnit({ dispatch }, { buyerId, unitId, data, params, refreshAll = false }) {
    const response = await updatePurchasingUnit(buyerId, unitId, data);
    try {
      if (refreshAll) await dispatch('fetchAllPurchasingUnits', { buyerId });
      else await dispatch('fetchPurchasingUnits', { buyerId, params });
    } catch {
      /* list action records refresh error */
    }
    return response.data;
  },
  async fetchUserPurchasingUnits({ commit }, { companyId, companyUserId }) {
    const response = await getUserPurchasingUnits(companyId, companyUserId);
    const payload = response.data || {};
    commit(types.USER_UNITS, payload.data || payload);
    return payload.data || payload;
  },
  async saveUserPurchasingUnits({ dispatch }, { companyId, companyUserId, purchasingUnitIds }) {
    const response = await updateUserPurchasingUnits(companyId, companyUserId, purchasingUnitIds);
    await dispatch('fetchUserPurchasingUnits', { companyId, companyUserId });
    return response.data;
  },
  async fetchEligiblePurchasingUnits({ commit }) {
    commit(types.ELIGIBLE_LOADING, true);
    commit(types.ELIGIBLE_ERROR, null);
    commit(types.ELIGIBLE, []);
    try {
      const response = await getEligiblePurchasingUnits();
      const units = response.data?.purchasingUnits || [];
      commit(types.ELIGIBLE, units);
      return units;
    } catch (error) {
      commit(types.ELIGIBLE_ERROR, error);
      throw error;
    } finally {
      commit(types.ELIGIBLE_LOADING, false);
    }
  },
  async fetchPurchasingUnitFilterOptions({ commit }) {
    commit(types.FILTER_OPTIONS_LOADING, true);
    commit(types.FILTER_OPTIONS_ERROR, null);
    try {
      const response = await getPurchasingUnitFilterOptions();
      const payload = response.data || {};
      const nestedPayload = payload.data && typeof payload.data === 'object' ? payload.data : null;
      let units = [];
      if (Array.isArray(payload)) units = payload;
      else if (Array.isArray(payload.purchasingUnits)) units = payload.purchasingUnits;
      else if (Array.isArray(nestedPayload?.purchasingUnits)) units = nestedPayload.purchasingUnits;
      else if (Array.isArray(nestedPayload)) units = nestedPayload;
      commit(types.FILTER_OPTIONS, units);
      const company = payload.company || nestedPayload?.company || null;
      commit(types.FILTER_OPTIONS_COMPANY, company);
      return { purchasingUnits: units, company };
    } catch (error) {
      commit(types.FILTER_OPTIONS_ERROR, error);
      throw error;
    } finally {
      commit(types.FILTER_OPTIONS_LOADING, false);
    }
  },
};

const getters = {
  purchasingUnits: state => state.units,
  purchasingUnitsPagination: state => state.pagination,
  purchasingUnitsLoading: state => state.loading,
  purchasingUnitsError: state => state.error,
  userPurchasingUnitIds: state =>
    state.userUnits.purchasingUnitIds || idsFrom(state.userUnits.purchasingUnits),
  eligiblePurchasingUnits: state => state.eligibleUnits,
  eligiblePurchasingUnitsLoading: state => state.eligibleLoading,
  eligiblePurchasingUnitsError: state => state.eligibleError,
  purchasingUnitFilterOptions: state => state.filterOptions,
  purchasingUnitFilterOptionsLoading: state => state.filterOptionsLoading,
  purchasingUnitFilterOptionsCompany: state => state.filterOptionsCompany,
  purchasingUnitFilterOptionsError: state => state.filterOptionsError,
};

export default {
  namespaced: true,
  state: {
    units: [],
    pagination: {},
    loading: false,
    error: null,
    userUnits: { purchasingUnitIds: [] },
    eligibleUnits: [],
    eligibleLoading: false,
    eligibleError: null,
    filterOptions: [],
    filterOptionsCompany: null,
    filterOptionsLoading: false,
    filterOptionsError: null,
  },
  mutations,
  actions,
  getters,
};
